import React, { useEffect, useRef, useState, useMemo } from 'react'
import {
  Box, Typography, Stack, LinearProgress, TableContainer, Table, TableBody, TableCell, TableRow,
  Menu, Tooltip, IconButton, Paper, Button, Chip, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { ClassNameMap, withStyles } from '@mui/styles'
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined'
import EditIcon from '@mui/icons-material/Edit'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditNoteIcon from '@mui/icons-material/EditNote';
import moment from 'moment'
import { useSelector } from 'react-redux'
import stylesheet from '../detail-container.stylesheet'
import { RootState } from '../../../../redux/store'
import NotesComponent from '../common/notes.component';
import TestHistoryComponent from '../conditions/test-history.component';
import AddTestDialogComponent from './add-test-dialog.component';
import { toast } from 'react-toastify';
import Helper from '../../../tasks/helper';
import { PatientService } from '../../../../services/patient.service';
import DeleteCardComponent from '../common/delete-card-component';
import { useUpdateDataAction } from '../../../../utils/update-details-hook';
import UploadTestResultComponent from './upload-test-result.component';
import DependentInfoComponent from '../common/dependent-info.component'
import TestDiagnosticImpactIndicator from '../common/test-diagnostic-impact-indicator.component'
import { formatDisplayName } from '../../../../utils/display-name-formatter'
import { isControlVisible } from '../comp-config'

interface IProps {
  input: any
  classes: ClassNameMap
}

const LazyAccordion: React.FC<{
  defaultExpanded: boolean
  label: any
  children: () => React.ReactNode
}> = ({ defaultExpanded, label, children }) => {
  const [hasOpened, setHasOpened] = useState(defaultExpanded)
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{ boxShadow: 'none', '&:before': { display: 'none' }, backgroundColor: 'transparent' }}
      onChange={(_, expanded) => {
        if (expanded) setHasOpened(true)
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.8rem !important' }} />}
        sx={{ minHeight: 28, px: 0.5, '& .MuiAccordionSummary-content': { margin: '4px 0' }, '& .MuiSvgIcon-root': { fontSize: '0.8rem !important' } }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.65rem !important', color: '#757575' }}>{label}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>{hasOpened && children()}</AccordionDetails>
    </Accordion>
  )
}

const DiagnosisTestsComponent: React.FC<IProps> = ({ input, classes, }) => {
  const [data, setData] = useState([] as any)
  const [loading, setLoading] = useState(false) as any
  const [infoDialog, setInfoDialog] = useState(false)
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  // Derive selectedRow from data so it always has the latest data (including conds_per_test)
  const selectedRow = useMemo(() => {
    if (!selectedRowId) return null;
    return data.find((item: any) => item.id === selectedRowId) || null;
  }, [selectedRowId, data]);
  const [anchorEl, setAnchorEl] = useState < null | HTMLElement > (null);
  const [selectedNotes, setSelectedNotes] = useState([] as number[])
  const [isAddTest, setIsAddTest] = useState(null) as any
  // No vendor state: each test carries its own Site (set by the navigator's "Screen and
  // Select testing site" task), and the backend groups by it. A picker here could disagree
  // with the card the provider is reading.
  const [labOrdering, setLabOrdering] = useState(false)
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const patientService = new PatientService()
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)

  useEffect(() => {
    if (!loading)
      initialiseData()
  }, [patientDetail?.diagnosis?.diagnostic_tests, input.isPatientList])

  const initialiseData = () => {
    if (input.isPatientList) {
      setLoading(true)
      patientService.getDiagnosticTests(input?.refId).then((res: any) => {
        if (res?.data?.diagnostic_tests)
          updateTestList(res.data.diagnostic_tests)

        setLoading(false)
      }).catch((err: any) => {
        setLoading(false)
        toast.error(Helper.getError(err), Helper.bottom_center())
      })
    } else {
      updateTestList(patientDetail?.diagnosis?.diagnostic_tests)
    }
  }

  const handleRowClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(row.id);
  };


  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateTestList = (currData: any) => {
    let testObj = (currData?.map((e: any) => { return { ...e, mode: 'view' } })) || []
    testObj = input?.isActionItems === true
      ? testObj?.filter((i: { needs_update: any, is_overridden: any, programs: any, overridden_programs: any }) => (
        i.needs_update === true &&
        i.programs?.some((s: string) => s === input?.actionProgram) &&
        !i.overridden_programs?.includes(input?.actionProgram)
      )) || []
      : testObj
    setData(testObj)
  }

  const getEnrollmentId = () => patientDetail?.program_enrollments
    ?.filter((m: any) => m.program?.includes('_evaluation'))
    ?.[0]?.enrollment_id || null

  const setIcons = (_type: string) => {
    return [
      { iconType: "edit", callback: onSetEdit },
      { iconType: "mark_deferred", callback: markDeferred },
      { iconType: "delete", callback: onSetDelete },
      { iconType: "visible", callback: onSetHistory },
      { iconType: "description", callback: onNotesClick },
      { iconType: "synopsis", callback: () => { } },
      { iconType: "more", callback: {} },
    ]
  }

  const onInfoClick = (id: string) => {
    const rowToDisplay = data.find((item: any) => item.id === id);
    if (rowToDisplay && rowToDisplay.id) {
      setSelectedRowId(rowToDisplay.id);
      setInfoDialog(true);
    }
  };

  const closeInfoDialog = () => {
    setInfoDialog(false)
    setSelectedRowId(null)
  }

  const onSetEdit = (id: string) => outer(id, "edit")
  const editTestByType = (id: string, type: string) => outer(id, type)
  const onSetHistory = (id: string) => outer(id, "history")
  const onSetDelete = (id: string) => outer(id, "delete")

  const markDeferred = (id: string) => {
    if (!id) return
    patientService.putDiagnosticTest(id, { status: 'deferred' })
      .then((res: any) => {
        if (res?.data) {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        }
      })
      .catch((err: any) => toast.error(Helper.getError(err), Helper.bottom_center()))
  }

  const outer = (id: string, mode: string) => {
    setIsAddTest(null)
    setData(data?.map((el: any) => (el.id === id ? { ...el, mode: mode } : el)))
    handleMenuClose()
  }

  const confirmDelete = (_id: any) => {
    patientService.deleteDiagnosticTest(patientDetail.id, _id)
      .then((res: any) => {
        (res?.data)
          ? updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
          : outer(_id, 'view')
      })
      .catch((err: any) => toast.error(Helper.getError(err), Helper.bottom_center()))
  }

  const onNotesClick = (_id: any) => {
    setSelectedNotes(selectedNotes.includes(+_id)
      ? [...selectedNotes.filter((f) => f !== +_id)]
      : [...selectedNotes, +_id]
    )
  };

  const submitNote = (id: any, note: any) => {
    if (id && note !== null) {
      patientService.putDiagnosticTest(id, { test_notes: note }).then((res: any) => {
        if (res?.data)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        onNotesClick(id)
      }).catch((error: any) => {
        onNotesClick(id)
        toast.error(Helper.getError(error), Helper.bottom_center())
      })
    } else
      onNotesClick(id)
  }

  const getClass = (item: any) => {
    let style
    switch (true) {
      case item?.is_referred === true:
        style = classes.referralBackground
        break
      case item?.is_overridden === true:
        style = classes.overrideBackground
        break
      case item?.needs_update === true:
        style = classes.confirmCardBackground
        break
      default:
        style = '-'
        break
    }
    return style
  }

  const getTestStatus = (status: string) => {
    const statusText = Helper.toCamelCase(status)?.toString().replaceAll('_', ' ') || status || '';
    return (
      <>
        {statusText.length > 12 ? (
          <Tooltip title={statusText}>
            <Typography className={classes.desc} variant="body2">
              <small>{statusText.length > 12 ? statusText.slice(0, 12) + "..." : statusText}</small>
            </Typography>
          </Tooltip>
        ) : (
          <Typography className={`${classes.desc}`} variant="body2">
            <small>{statusText ? statusText : null}</small>
          </Typography>
        )}
      </>
    )
  }

  const getDiaTestList = () => {
    const statusOrder = ['recommended', 'order_preparation', 'ordered', 'order_notified', 'order_scheduled', 'order_follow_up', 'results_available', 'deferred'];

    const sortByStatus = (a: any, b: any) => {
      const statusIndexA = statusOrder.indexOf(a?.current_status) !== -1
        ? statusOrder.indexOf(a?.current_status) : statusOrder.length;
      const statusIndexB = statusOrder.indexOf(b?.current_status) !== -1
        ? statusOrder.indexOf(b?.current_status) : statusOrder.length;
      if (statusIndexA !== statusIndexB) return statusIndexA - statusIndexB;
      const fullNameA = (formatDisplayName(a?.display_name) || a?.name || '').toLowerCase();
      const fullNameB = (formatDisplayName(b?.display_name) || b?.name || '').toLowerCase();
      return fullNameA.localeCompare(fullNameB);
    }

    // Split into recommended (EIG-ranked) and other tests
    const recommendedTests = [...data]
      .filter((t: any) => t.recommendation_tier === 'recommended')
      .sort((a: any, b: any) => (a.recommendation_rank || 999) - (b.recommendation_rank || 999))
    const otherTests = [...data]
      .filter((t: any) => t.recommendation_tier !== 'recommended')
      .sort(sortByStatus)

    // Extract sequence pairs from persisted sequence data on individual tests
    const sequencePairs: any[] = []
    const seenPartners = new Set<string>()
    for (const t of data) {
      if (t.sequence?.role === 'screen' && !seenPartners.has(t.name)) {
        const partner = data.find((p: any) => p.name === t.sequence.partner)
        if (partner) {
          seenPartners.add(partner.name)
          sequencePairs.push({
            screenTest: t,
            confirmTest: partner,
            inb: t.sequence.sequence_inb || 0,
            rank: t.sequence.rank || 999,
          })
        }
      }
    }
    sequencePairs.sort((a: any, b: any) => a.rank - b.rank)

    const renderSequenceRow = (pair: any, i: number) => (
      <TableRow
        key={`seq-${i}`}
        sx={{
          backgroundColor: 'var(--color-badge-purple-bg)',
          '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.08)' },
        }}
      >
        <TableCell className={classes.tablePaddingPT} colSpan={2} sx={{ width: '72%' }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Chip
              size="small"
              label="SEQ"
              sx={{
                fontSize: '0.55rem', height: 18,
                backgroundColor: '#ce93d8', color: '#fff',
                fontWeight: 700,
              }}
            />
            <Typography variant="body2" className={classes.desc}>
              {formatDisplayName(pair.screenTest?.display_name) || pair.screenTest?.name}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: '0.8rem', color: '#7b1fa2' }} />
            <Typography variant="body2" className={classes.desc}>
              {formatDisplayName(pair.confirmTest?.display_name) || pair.confirmTest?.name}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: '#7b1fa2', fontSize: '0.6rem', pl: 4.5 }}>
            Screen then confirm &bull; INB: {pair.inb?.toFixed(3)} QALY
          </Typography>
        </TableCell>
        <TableCell className={classes.tablePaddingPT} sx={{ width: '16%', verticalAlign: 'top' }}>
          <Typography variant="body2" className={classes.smallDesc}>
            <small>
              ${Math.round(pair.screenTest?.test_cost || pair.screenTest?.cost_estimate || 0)} + ${Math.round(pair.confirmTest?.test_cost || pair.confirmTest?.cost_estimate || 0)}
            </small>
          </Typography>
        </TableCell>
        <TableCell className={classes.tablePaddingPT} sx={{ width: '12%' }} />
      </TableRow>
    )

    const getSourceBadge = (source: string) => {
      if (!source || source === 'inb') return null
      const config: Record<string, { label: string; bg: string; color: string }> = {
        urgent_injection: { label: 'URGENT', bg: '#ffebee', color: '#c62828' },
        cardiac_injection: { label: 'CARDIAC', bg: '#e3f2fd', color: '#1565c0' },
        characteristic_injection: { label: 'CHAR', bg: '#e8f5e9', color: '#2e7d32' },
      }
      const cfg = config[source]
      if (!cfg) return null
      return (
        <Chip
          size="small"
          label={cfg.label}
          sx={{
            fontSize: '0.5rem', height: 16,
            backgroundColor: cfg.bg, color: cfg.color,
            fontWeight: 700,
          }}
        />
      )
    }

    const renderTestRow = (row: any, i: number, showRank: boolean) => (
      <React.Fragment key={`test-${row.id}-${i}`}>
        <TableRow
          onClick={(e) => handleRowClick(e, row)}
          className={[getClass(row), classes.zeroPadding].join(' ')}
          sx={{
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          <TableCell className={classes.tablePaddingPT} scope="row" sx={{ width: showRank ? '52%' : '64%' }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" className={classes.desc}>
                {formatDisplayName(row?.display_name) || row?.name}
              </Typography>
              {showRank && row.recommendation_rank < 999 && (
                <Chip
                  size="small"
                  label={`#${row.recommendation_rank}`}
                  sx={{
                    fontSize: '0.6rem', height: 18,
                    backgroundColor: '#e3f2fd', color: '#1976d2',
                    fontWeight: 600,
                  }}
                />
              )}
              {getSourceBadge(row.source)}
            </Stack>
          </TableCell>
          <TableCell className={classes.tablePaddingPT} scope="row" sx={{ width: '20%', verticalAlign: 'top' }}>
            {getTestStatus(row.current_status)}
          </TableCell>
          {showRank && (
            <TableCell className={classes.tablePaddingPT} scope="row" sx={{ width: '12%', verticalAlign: 'top' }}>
              <Typography variant="body2" className={classes.smallDesc}>
                <small>{row.test_cost > 0 ? `$${Math.round(row.test_cost)}` : ''}</small>
              </Typography>
            </TableCell>
          )}
          <TableCell className={classes.tablePaddingPT} scope="row" sx={{ width: '12%', verticalAlign: 'top' }}>
            <Typography variant="body2" className={classes.smallDesc}>
              <small>{moment.unix(row.date_recorded).format('MM/DD/YY')}</small>
            </Typography>
          </TableCell>
        </TableRow>

        {/* Show dialogs/components directly below the row */}
        {(['edit'].includes(row.mode) || row.mode.includes('mark')) && (
          <TableRow>
            <TableCell colSpan={showRank ? 4 : 3} sx={{ padding: 0 }}>
              <UploadTestResultComponent
                open={true}
                input={{
                  title: formatDisplayName(row.display_name) || row.name,
                  id: row.id,
                  test_name: row.name,
                  test_notes: row.test_notes || '',
                  status: row.current_status,
                  site: row?.site || {},
                  mark_status: (row.mode) === 'edit'
                    ? ''
                    : (row.mode) === 'mark_result'
                      ? 'results_available'
                      : (row.mode) === 'mark_order_preparation'
                        ? 'order_preparation'
                        : (row.mode) === 'mark_order_follow_up'
                          ? 'order_follow_up'
                          : (row.mode).includes('mark')
                            ? row.mode.replaceAll('mark_', '')
                            : row.mode,
                  scheduled_date: row?.scheduled_date || '',
                  order_completed_date: row?.order_completed_date || '',
                  date_recorded: row?.date_recorded || '',
                  result_source: row?.result_source || '',
                  insurance_approval_status: row?.insurance_approval_status || '',
                  enrollment_id: (row?.enrollment_ids || '').toString(),
                }}
                handleDialogClose={() => outer(row.id, 'view')}
              />
            </TableCell>
          </TableRow>
        )}

        {row.mode === 'delete' && (
          <TableRow>
            <TableCell colSpan={showRank ? 4 : 3} sx={{ padding: 0 }}>
              <DeleteCardComponent
                id={row.id}
                header={formatDisplayName(row.display_name) || row.name}
                closeDelete={() => outer(row.id, 'view')}
                outerDelete={() => confirmDelete(row.id)}
              />
            </TableCell>
          </TableRow>
        )}

        {['history'].includes(row.mode) && (
          <TableRow>
            <TableCell colSpan={showRank ? 4 : 3} sx={{ padding: 0 }}>
              <Box className={classes.fullWidth}>
                <TestHistoryComponent
                  input={{
                    id: row.id,
                    current_status: row.current_status,
                    type: row.mode,
                    status_history: row?.status_history || []
                  }}
                  name={formatDisplayName(row.display_name) || row.name}
                  outer={outer}
                />
              </Box>
            </TableCell>
          </TableRow>
        )}

        {selectedNotes.includes(row.id) && (
          <TableRow>
            <TableCell colSpan={showRank ? 4 : 3} sx={{ padding: 0 }}>
              <NotesComponent
                entityId={row.id}
                entityRef={"diagnostic_test"}
                currNote={row.test_notes || ''}
                cancelBtn={true}
                handleSubmit={submitNote}
                onCancel={onNotesClick}
              />
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    )

    // BATCH LAB ORDER — one requisition per SITE, for many tests.
    //
    // The per-test "Order in Elation" is right for imaging (one study, one centre) but
    // means eight signatures for eight labs (RD 2026-07-19: "it's tedious to fax 8
    // different orders"). The receiving site consolidates the draws itself, so batching is
    // purely about provider burden.
    //
    // NO VENDOR PICKER: each test already carries its own Site, chosen by the navigator's
    // "Screen and Select testing site" task and shown on the test card. The backend groups
    // by that Site and emits one order per group — asking here would invite a choice that
    // disagrees with the card. The UI also does NOT pre-filter to labs: it has no category
    // field, and guessing would silently drop a test. Routing is the backend's job.
    const renderBatchLabOrder = () => {
      // Gate on ANY test, not just EIG-'recommended' ones: a patient can have tests
      // sitting entirely in the 'All Tests' bucket (recommendation_tier unset), which
      // is exactly when the button is most useful. Gating on recommendedTests hid it.
      if (!data?.length) return null
      // Deployments with no write path to their EHR cannot complete this action.
      if (!isControlVisible('order_all_labs')) return null
      const send = async () => {
        if (labOrdering) return
        setLabOrdering(true)
        try {
          const ids = data.map((t: any) => t.id)
          const resp = await patientService.pushLabOrderToElation(patientDetail.id, ids)
          const body = resp?.data
          const skipped = (body?.skipped || []).length
          toast.success(
            `${body?.message || 'Draft lab order created'}${skipped ? ` — ${skipped} skipped` : ''}`,
            Helper.bottom_center())
        } catch (error: any) {
          const err = error.response?.data?.error
          // Name every unmapped test AND its site, so one pass fixes them all.
          const detail = (err?.failures || [])
            .map((f: any) => `${f.test}${f.site ? ` @ ${f.site}` : ''}`).join(', ')
          toast.error(
            `${err?.message || 'Failed to create lab order'}${detail ? `: ${detail}` : ''}`,
            Helper.bottom_center())
        } finally {
          setLabOrdering(false)
        }
      }
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Tooltip title="Create one Elation draft per testing site for all lab tests">
            <span>
              <Button
                size="small"
                variant="text"
                disabled={labOrdering}
                startIcon={<ReceiptLongIcon sx={{ fontSize: '0.7rem !important' }} />}
                onClick={send}
                sx={{
                  fontSize: '0.62rem', py: 0, px: 0.75, minWidth: 0, lineHeight: 1.4,
                  textTransform: 'none',
                }}
              >
                {labOrdering ? 'Ordering…' : 'Order all labs'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )
    }

    return (
      <Box>
        {renderBatchLabOrder()}
        {/* Recommended Tests section */}
        {recommendedTests.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.65rem !important', color: '#1976d2', mb: 0.5, px: 0.5 }}>
              Recommended Tests ({recommendedTests.length})
            </Typography>
            <TableContainer component={Paper}>
              <Table className={classes.table} aria-label="recommended diagnostic tests">
                <TableBody className={classes.historyData}>
                  {recommendedTests.map((row: any, i: number) => renderTestRow(row, i, true))}
                  {/* Sequences hidden until data quality is verified */}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Sequence-only section hidden until data quality is verified */}

        {/* Other Available Tests section */}
        {otherTests.length > 0 && (
          <LazyAccordion
            defaultExpanded={recommendedTests.length === 0 && otherTests.length <= 15}
            label={recommendedTests.length > 0 ? `Other Available Tests (${otherTests.length})` : `All Tests (${otherTests.length})`}
          >
            {() => (
              <TableContainer component={Paper}>
                <Table className={classes.table} aria-label="other diagnostic tests">
                  <TableBody className={classes.historyData}>{otherTests.map((row: any, i: number) => renderTestRow(row, i, false))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </LazyAccordion>
        )}

        {/* Action Menu for clicked row */}
        <Menu
          id="compact-menu"
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {selectedRow && selectedRow.id && (
            <Stack spacing={0.4} direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ padding: '0px 4px !important', height: '0.7rem !important' }}>
              {setIcons(selectedRow.current_status)
                .filter((icon: any) => icon.iconType !== 'more')
                .map((icon: any, index: number) => {
                  const getCompactIcons = (iconType: string) => {
                    switch (iconType) {
                      case 'edit':
                        return <EditIcon className={classes.editIcon} />
                      case 'mark_order_preparation':
                        return <NoteAddIcon className={classes.editIcon} />
                      case 'mark_order_follow_up':
                        return <StickyNote2Icon className={classes.editIcon} />
                      case 'mark_order_notified':
                        return <StickyNote2Icon className={classes.editIcon} />
                      case 'mark_order_scheduled':
                        return <StickyNote2Icon className={classes.editIcon} />
                      case 'mark_deferred':
                        return <HourglassBottomIcon className={classes.editDefIcon} />
                      case 'mark_ordered':
                        return <ReceiptLongIcon className={classes.editIcon} />
                      case 'mark_result':
                        return <ContentPasteGoIcon className={classes.noteIcon} />
                      case 'description':
                        return <InsertDriveFileOutlinedIcon className={classes.noteIcon} />
                      case 'visible':
                        return <VisibilityOutlinedIcon className={classes.editIcon} />
                      case 'delete':
                        return <DeleteOutlinedIcon className={classes.editIcon} />
                      case 'synopsis':
                        return <EditNoteIcon className={classes.editIcon} />
                      case 'dependent_info':
                        return <AssignmentIcon className={classes.noteIcon} />
                      default:
                        return <span></span>
                    }
                  }

                  const getTooltipTitle = (iconType: string) => {
                    if (iconType.includes('mark')) {
                      return iconType.charAt(0).toUpperCase() + iconType.slice(1).replaceAll('_', ' ')
                    }
                    if (iconType === 'synopsis') {
                      return selectedRow?.synopsis || 'Synopsis'
                    }
                    if (iconType === 'edit') return 'Edit'
                    if (iconType === 'delete') return 'Delete'
                    if (iconType === 'visible') return 'View History'
                    if (iconType === 'description') return 'Notes'
                    if (iconType === 'dependent_info') return 'Info'
                    return ''
                  }

                  return (
                    <Tooltip key={`tooltip-${index}`} title={getTooltipTitle(icon.iconType)} placement="bottom" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()

                          if (icon.iconType === 'synopsis') {
                            handleMenuClose()
                            return
                          }

                          if (icon.iconType.includes('mark')) {
                            icon.callback(selectedRow.id, icon.iconType)
                          } else {
                            icon.callback(selectedRow.id)
                          }

                          handleMenuClose()
                        }}
                        className={classes.zeroPadding}
                      >
                        {getCompactIcons(icon.iconType)}
                      </IconButton>
                    </Tooltip>
                  )
                })}
              {/* Diagnostic Impact Indicator */}
              {selectedRow?.conds_per_test?.length > 0 && <TestDiagnosticImpactIndicator condsPerTest={selectedRow.conds_per_test} />}
            </Stack>
          )}
        </Menu>
      </Box>
    )
  }

  return (
    <>
      {loading && <LinearProgress />}
      <Stack direction="column" className={classes.medIntoleranceContainer}>
        {getDiaTestList()}
      </Stack>
      {isAddTest !== null &&
        <>
          {isAddTest === 'results_available'
            ? <UploadTestResultComponent
              open={true}
              input={{
                isAdd: true,
                title: 'Add result',
                status: 'results_available',
                enrollment_id: getEnrollmentId()
              }}
              handleDialogClose={() => setIsAddTest(null)}
            />
            : <AddTestDialogComponent
              open={Boolean(isAddTest)}
              input={{
                title: `Add ${isAddTest.replaceAll('_', ' ')}`,
                isAdd: true,
                type: isAddTest,
                enrollment_id: getEnrollmentId(),
              }}
              handleDialogClose={() => setIsAddTest(null)}
            />
          }
        </>
      }
      {infoDialog && selectedRow?.id && <DependentInfoComponent
        open={infoDialog}
        input={{
          title: formatDisplayName(selectedRow?.display_name) || selectedRow?.name || '',
          isDiagnosticTest: true,
          programs: selectedRow?.programs || [],
          dependent_questionnaires: [],
          tests_per_cond: [],
          conditions: selectedRow?.conds_per_test || [],
          threshold: [],
          category: 'diagnosis'
        }}
        handleClose={closeInfoDialog}
      />
      }
    </>
  )
}

export default withStyles(stylesheet)(DiagnosisTestsComponent)